import { useState, useRef, useEffect, useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DateTimePicker } from "@/components/ui/DateTimePicker";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import {
  Plus,
  Calendar,
  Users,
  Edit,
  Trash2,
  ExternalLink,
  Search,
  Upload,
  Percent,
  DollarSign,
  Tag,
  X,
  Eye,
  Camera,
  MoreVertical,
  Trash2 as TrashIcon,
} from "lucide-react";
import { useCampaignManagement } from "@/hooks/useCampaignManagement";
import { useListingManagement } from "@/hooks/useListingManagement";
import { useQueryClient } from "@tanstack/react-query";
import { formatMileage } from "@/utils/listingUtils";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pagination } from "@/components/ui/pagination";
import { FileObject } from "@/types/shared/file";
// Removed direct api import - use hooks/services instead
import {
  getTypeColor,
  getTypeLabel,
  getStatusColor,
  getStatusLabel,
  getVehicleStatusColor,
  getVehicleStatusLabel,
  getCampaignStatus,
} from "@/utils/listingUtils";

import { ListingDetailsPopup } from "./ListingDetailsPopup";
import { CreateListingDialog } from "./CreateListingDialog";
import { ListingSelectionStep } from "./ListingSelectionStep";
import {
  SUPPORTED_IMAGE_TYPES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
} from "@/config/constants";
import { useLanguage } from "@/context/LanguageContext";
import { FileUploadTrigger } from "@/components/native/FileUploadTrigger";
import { adminLabels } from "@/i18n/labels/adminLabels";
import { commonButtonLabels } from "@/i18n/labels/commonLabels";
import {
  leadboxSheetContentClass,
  leadboxSheetSide,
} from "@/lib/native/nativeLeadboxSheet";
import {
  NativeActionMenu,
  useShouldUseNativeActionMenu,
} from "@/components/native/NativeActionMenu";

interface CampaignDetailsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  campaignId: string | null;
}

// TODO: Fix so that we read all listings from the campaign item from campaign response

export const CampaignDetailsPopup = ({
  isOpen,
  onClose,
  campaignId,
}: CampaignDetailsPopupProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showCreateListing, setShowCreateListing] = useState(false);
  const [showVehicleSelection, setShowVehicleSelection] = useState(false);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string>("");
  const openImagePickerRef = useRef<(() => void) | null>(null);
  const [selectedListingId, setSelectedListingId] = useState<string | null>(
    null,
  );
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelInfo, setCancelInfo] = useState<{
    activeAuctionsWithBids: Array<{ title: string; bidCount: number }>;
  } | null>(null);
  const [showCancelListingDialog, setShowCancelListingDialog] = useState(false);
  const [cancelListingInfo, setCancelListingInfo] = useState<{
    listingId: string;
    listingTitle: string;
    bidCount: number;
    hasActiveBids: boolean;
    listingType: string;
  } | null>(null);
  const [showStartDateChangeDialog, setShowStartDateChangeDialog] =
    useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState<any>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    thumbnailImage: null as File | null,
    thumbnailPreview: "",
    shouldRemoveImage: false,
    labels: [] as {
      labelType: "reduced_percentage" | "reduced_amount" | "special";
      text: string;
    }[],
  });
  const navigate = useNavigate();
  const useNativeActionMenu = useShouldUseNativeActionMenu();
  const { language } = useLanguage();
  const L = (adminLabels(language) as any)?.leadboxListings ?? {};
  const campaignDateLocale = language === "en" ? undefined : sv;
  const formatCampaignDateTime = (value: string) =>
    format(new Date(value), "yyyy-MM-dd HH:mm", {
      locale: campaignDateLocale,
    });
  const [listingsPage, setListingsPage] = useState(1);
  const listingsLimit = 10;

  // Status filter: empty selection = "Alla" (same pattern as leadbox).
  // "Alla" is mutually exclusive with specific statuses; specifics can multi-select.
  const LISTING_STATUS_OPTIONS: { key: string; label: string }[] = [
    { key: "active", label: L.active },
    { key: "reserved", label: L.reserved },
    { key: "draft", label: L.draft },
    { key: "completed", label: L.completed },
    { key: "ended", label: L.ended },
    { key: "cancelled", label: L.cancelled ?? "Avbruten" },
  ];
  const ALL_LISTING_STATUS_KEYS = LISTING_STATUS_OPTIONS.map((o) => o.key);
  /** Empty array = Alla (show every status). */
  const [selectedListingStatuses, setSelectedListingStatuses] = useState<
    string[]
  >([]);
  const listingStatuses = useMemo(
    () =>
      (selectedListingStatuses.length === 0
        ? ALL_LISTING_STATUS_KEYS
        : selectedListingStatuses
      ).map((key) => key.toUpperCase()),
    [selectedListingStatuses],
  );
  const isAllListingStatuses = selectedListingStatuses.length === 0;
  const toggleListingStatus = (status: string) => {
    if (status === "all") {
      setSelectedListingStatuses([]);
      setListingsPage(1);
      return;
    }
    setSelectedListingStatuses((prev) => {
      const next = prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status];
      return next;
    });
    setListingsPage(1);
  };

  const {
    updateCampaign,
    updateCampaignImage,
    removeCampaignImage,
    isUpdating,
    isUpdatingImage,
    isRemovingImage,
    addListingToCampaign,
    removeListingFromCampaign,
    cancelCampaign,
    activateCampaign,
    isCancelling,
    isActivating,
    campaign,
    listings,
    totalListings,
    isLoadingCampaign,
    campaignError,
  } = useCampaignManagement(
    1,
    10,
    campaignId || "",
    listingsPage,
    listingsLimit,
    listingStatuses,
  );

  const { cancelListingAsync, isCancellingListing, getCancelListingPreview } =
    useListingManagement();
  const queryClient = useQueryClient();

  // Backend returns listings filtered by listingStatuses; no client-side filter
  const displayListings = listings ?? [];
  // For start-date change: only active/reserved/draft would be cancelled (count from current page)
  const activeReservedDraftCount = displayListings.filter(
    (l) => !["cancelled", "completed", "ended"].includes(l.status),
  ).length;
  const totalPages = Math.ceil(totalListings / listingsLimit);

  // Sync editForm with campaign data when campaign changes
  useEffect(() => {
    if (campaign) {
      setEditForm({
        name: campaign.name ?? "",
        description: campaign.description ?? "",
        startDate: new Date(campaign.startDate).toISOString(),
        endDate: new Date(campaign.endDate).toISOString(),
        thumbnailImage: null,
        thumbnailPreview: campaign.thumbnailImage?.url || "",
        shouldRemoveImage: false,
        labels: (campaign.labels ?? []).map((label) => ({
          ...label,
          text: label.text ?? "",
        })),
      });
    }
  }, [campaign, isLoadingCampaign]);

  // Always default status filter to "Alla" when opening a campaign dialog
  useEffect(() => {
    if (isOpen) {
      setSelectedListingStatuses([]);
      setListingsPage(1);
    }
  }, [isOpen, campaignId]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!campaign) return;

    try {
      // Filter out empty labels
      const filteredLabels = editForm.labels.filter(
        (label) => label.text.trim() !== "",
      );

      // Check if start date is being changed
      const originalStartDate = new Date(campaign.startDate).toISOString();
      const newStartDate = new Date(editForm.startDate).toISOString();
      const isStartDateChanging = originalStartDate !== newStartDate;

      // If start date is changing, check if there are active/draft/reserved listings
      if (isStartDateChanging && activeReservedDraftCount > 0) {
        // Show confirmation dialog
        const updateData = {
          _id: campaign._id,
          name: editForm.name,
          description: editForm.description,
          startDate: editForm.startDate,
          endDate: editForm.endDate,
          mediaId: undefined as string | null | undefined,
          type: campaign.type,
          targetUserType: campaign.targetUserType,
          labels: filteredLabels,
        };
        setPendingUpdateData(updateData);
        setShowStartDateChangeDialog(true);
        return;
      }

      // Handle image removal or upload
      let finalMediaId: string | null | undefined = undefined;

      if (editForm.shouldRemoveImage) {
        // Remove image via hook/service
        if (campaign.thumbnailImage?.id) {
          try {
            await removeCampaignImage({
              campaignId: campaign._id,
              mediaId: campaign.thumbnailImage.id,
            });
          } catch (error) {
            toast.error(L.couldNotRemoveImage);
            return; // Don't continue if deletion fails
          }
        }
        finalMediaId = null; // Set mediaId to null to disconnect
      } else if (editForm.thumbnailImage) {
        // Upload new image (lazy upload - upload when saving)
        try {
          const response = await updateCampaignImage({
            campaignId: campaign._id,
            request: { image: editForm.thumbnailImage },
          });
          finalMediaId = response.id;
        } catch (error) {
          toast.error(L.couldNotUploadImage);
          return; // Don't continue if image upload fails
        }
      }
      // If neither shouldRemoveImage nor thumbnailImage, finalMediaId stays undefined (no change)

      // Send whole object for PUT (like we do with listings)
      const updateData = {
        _id: campaign._id,
        name: editForm.name,
        description: editForm.description,
        startDate: editForm.startDate,
        endDate: editForm.endDate,
        mediaId: finalMediaId,
        type: campaign.type, // Keep original type
        targetUserType: campaign.targetUserType, // Keep original targetUserType
        labels: filteredLabels,
      };

      await updateCampaign(updateData);

      toast.success(L.campaignUpdated);
      setIsEditing(false);
      // No need to refetch - useCampaignManagement automatically invalidates queries
    } catch (error) {
      toast.error(L.campaignUpdateError);
    }
  };

  const handleConfirmStartDateChange = async () => {
    if (!pendingUpdateData || !campaign) return;

    try {
      // Handle image removal or upload
      let finalMediaId: string | null | undefined = undefined;

      if (editForm.shouldRemoveImage) {
        if (campaign.thumbnailImage?.id) {
          try {
            await removeCampaignImage({
              campaignId: campaign._id,
              mediaId: campaign.thumbnailImage.id,
            });
          } catch (error) {
            toast.error(L.couldNotRemoveImage);
            return;
          }
        }
        finalMediaId = null;
      } else if (editForm.thumbnailImage) {
        try {
          const response = await updateCampaignImage({
            campaignId: campaign._id,
            request: { image: editForm.thumbnailImage },
          });
          finalMediaId = response.id;
        } catch (error) {
          toast.error(L.couldNotUploadImage);
          return;
        }
      }

      const updateData = {
        ...pendingUpdateData,
        mediaId: finalMediaId,
      };

      await updateCampaign(updateData);

      toast.success(L.campaignUpdatedCancelledListings);
      setIsEditing(false);
      setShowStartDateChangeDialog(false);
      setPendingUpdateData(null);
    } catch (error) {
      toast.error(L.campaignUpdateError);
    }
  };

  const handleCancelListingClick = async (
    listingId: string,
    listingTitle: string,
    listingStatus: string,
    listingType: string,
  ) => {
    // Don't allow cancellation of COMPLETED (SOLD) or CANCELLED listings
    // RESERVED listings can now be cancelled
    if (listingStatus === "completed" || listingStatus === "cancelled") {
      toast.error(
        "Kan inte avbryta en annons som är såld eller redan avbruten",
      );
      return;
    }

    try {
      // Fetch cancel preview to get info about bids using hook
      const preview = await getCancelListingPreview(listingId);
      setCancelListingInfo({
        listingId,
        listingTitle,
        bidCount: preview.bidCount,
        hasActiveBids: preview.hasActiveBids,
        listingType: preview.listingType,
      });
      setShowCancelListingDialog(true);
    } catch (error) {
      toast.error("Ett fel uppstod vid förhandsvisning av avbrytning");
    }
  };

  const handleConfirmCancelListing = async () => {
    if (!cancelListingInfo) return;

    try {
      await cancelListingAsync(cancelListingInfo.listingId);
      toast.success("Annonsen har avbrutits!");
      setShowCancelListingDialog(false);
      setCancelListingInfo(null);
      // Invalidate queries to refresh campaign listings
      queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["admin-listings"] });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Ett fel uppstod vid avbrytning av annons";
      toast.error(errorMessage);
    }
  };

  const handleRemoveVehicle = async (vehicleId: string) => {
    if (!campaignId) return;

    try {
      await removeListingFromCampaign({
        campaignId,
        listingId: vehicleId,
      });
      toast.success("Fordon taget bort från kampanjen!");
      // React Query will automatically refetch campaign data via query invalidation
    } catch (error) {
      toast.error("Ett fel uppstod vid borttagning av fordon");
    }
  };

  const handleActivateClick = async () => {
    if (!campaignId || !campaign) return;

    try {
      await activateCampaign(campaignId);
      toast.success("Kampanj aktiverad");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        "Ett fel uppstod vid aktivering av kampanj";
      toast.error(errorMessage);
    }
  };

  const handleCancelClick = async () => {
    if (!campaignId || !campaign) return;

    try {
      // Fetch cancel preview to get info about active auctions with bids
      const { CampaignManagementService } =
        await import("@/services/campaign/campaignManagementService");
      const preview =
        await CampaignManagementService.getCancelPreview(campaignId);
      setCancelInfo({
        activeAuctionsWithBids: preview.activeAuctionsWithBids,
      });
      setShowCancelDialog(true);
    } catch (error) {
      // Still show dialog even if preview fails
      setShowCancelDialog(true);
    }
  };

  const handleConfirmCancel = async () => {
    if (!campaignId || !campaign) return;

    try {
      const response = await cancelCampaign(campaignId);

      // Backend returns activeAuctionsWithBids in response
      if (
        response?.activeAuctionsWithBids &&
        response.activeAuctionsWithBids.length > 0
      ) {
        // If there are active auctions with bids, show info but campaign is already cancelled
        setCancelInfo({
          activeAuctionsWithBids: response.activeAuctionsWithBids,
        });
        toast.success("Kampanjen har avbrutits!");
        // Don't close dialog immediately - let user see the info
        setTimeout(() => {
          setShowCancelDialog(false);
          setCancelInfo(null);
          onClose();
        }, 3000);
      } else {
        toast.success("Kampanjen har avbrutits!");
        setShowCancelDialog(false);
        setCancelInfo(null);
        onClose(); // Close the popup after successful cancellation
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Ett fel uppstod vid avbrytning av kampanj";
      toast.error(errorMessage);
      setShowCancelDialog(false);
      setCancelInfo(null);
    }
  };

  const handleAddListingToCampaign = async (listingId: string) => {
    if (!campaignId) return;

    try {
      await addListingToCampaign({ campaignId, listingId });
      toast.success("Fordon lagt till i kampanjen!");
      // React Query optimistic updates handle UI updates automatically
    } catch (error) {
      toast.error("Ett fel uppstod vid tillägg av fordon"); // React Query will automatically revert optimistic update on error
    }
  };

  const handleGoToListing = (vehicleId: string) => {
    navigate(`/auctions/${campaignId}/${vehicleId}`, {
      state: { from: "/admin/leadbox/listings" },
    });
  };

  const handleShowVehicle = (vehicleId: string) => {
    setSelectedListingId(vehicleId);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      // Validate file type using constants
      if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
        const allowedExtensions = SUPPORTED_IMAGE_TYPES.map((t) => {
          const ext = t.split("/")[1].toUpperCase();
          return ext === "JPEG" || ext === "JPG" ? "JPEG" : ext;
        }).filter((v, i, a) => a.indexOf(v) === i); // Remove duplicates
        toast.error(
          `Ogiltigt filformat. Endast ${allowedExtensions.join(", ")} tillåts.`,
        );
        event.target.value = ""; // Reset file input
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`Filen är för stor. Max ${MAX_FILE_SIZE_MB} tillåtet.`);
        event.target.value = ""; // Reset file input
        return;
      }

      // Store file locally for lazy upload (upload when saving)
      setEditForm((prev) => ({
        ...prev,
        thumbnailImage: file,
        shouldRemoveImage: false,
      }));

      // Create preview URL for display
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditForm((prev) => ({
          ...prev,
          thumbnailPreview: e.target?.result as string,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setEditForm((prev) => ({
      ...prev,
      thumbnailImage: null,
      thumbnailPreview: "",
      shouldRemoveImage: true,
    }));
    toast.success("Bild kommer att tas bort när du sparar ändringarna");
  };

  // Image handling functions
  const handleViewImage = (imageUrl: string) => {
    setPreviewImageUrl(imageUrl);
    setIsImagePreviewOpen(true);
  };

  const handleChangeImage = () => {
    openImagePickerRef.current?.();
  };

  const predefinedLabels = [
    {
      key: "reduced_percentage",
      label: "Rabatt %",
      icon: Percent,
      color: "bg-red-100 text-red-800",
    },
    {
      key: "reduced_amount",
      label: "Rea",
      icon: DollarSign,
      color: "bg-green-100 text-green-800",
    },
    {
      key: "special",
      label: "Special",
      icon: Tag,
      color: "bg-blue-100 text-blue-800",
    },
  ];

  const handleAddLabel = () => {
    if (editForm.labels.length < 2) {
      setEditForm((prev) => ({
        ...prev,
        labels: [...prev.labels, { labelType: "reduced_percentage", text: "" }],
      }));
    }
  };

  const handleRemoveLabel = (index: number) => {
    if (editForm.labels.length > 0) {
      setEditForm((prev) => ({
        ...prev,
        labels: prev.labels.filter((_, i) => i !== index),
      }));
    }
  };

  const handleLabelChange = (
    index: number,
    field: "labelType" | "text",
    value: string,
  ) => {
    if (field === "text" && value.length > 10) return;

    setEditForm((prev) => {
      const newLabels = [...prev.labels];
      newLabels[index] = { ...newLabels[index], [field]: value };
      return { ...prev, labels: newLabels };
    });
  };

  if (isLoadingCampaign) {
    return (
      <Sheet
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <SheetContent
          side={leadboxSheetSide()}
          className={leadboxSheetContentClass("sm:max-w-[800px]")}
        >
          <SheetHeader>
            <SheetTitle className="text-2xl font-bold">
              {L.loadingCampaign}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6 text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground mt-4">
              {L.loadingCampaignDetails}
            </p>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (!campaign) {
    return (
      <Sheet
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <SheetContent
          side={leadboxSheetSide()}
          className={leadboxSheetContentClass("sm:max-w-[800px]")}
        >
          <SheetHeader>
            <SheetTitle className="text-2xl font-bold">
              {L.campaignNotFound}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6 text-center py-8">
            <p className="text-muted-foreground">{L.campaignNotFoundDesc}</p>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <>
      <Sheet
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <SheetContent
          side={leadboxSheetSide()}
          className={leadboxSheetContentClass("sm:max-w-[800px]")}
        >
          <SheetHeader>
            <SheetTitle className="text-2xl font-bold">
              {campaign.name}
            </SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Campaign Information Card */}
            <Card className="p-6 relative">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Badge className={getTypeColor(campaign.type)}>
                    {getTypeLabel(campaign.type)}
                  </Badge>
                  <Badge className={getStatusColor(campaign.status)}>
                    {getStatusLabel(campaign.status)}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={handleEditClick} variant="outline" size="sm">
                    <Edit className="h-4 w-4 mr-1" />
                    {commonButtonLabels(language)?.edit ?? "Redigera"}
                  </Button>
                  {campaign.status === "cancelled" ? (
                    <Button
                      onClick={handleActivateClick}
                      variant="default"
                      size="sm"
                      disabled={isActivating}
                    >
                      {isActivating ? L.activating : L.activateCampaign}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleCancelClick}
                      variant="destructive"
                      size="sm"
                      disabled={campaign.status === "completed"}
                    >
                      {L.cancel ?? "Avbryt"}
                    </Button>
                  )}
                </div>
              </div>

              <h3 className="text-lg font-semibold mb-2">{L.campaignInfo}</h3>
              <p className="text-muted-foreground mb-4 whitespace-pre-wrap">
                {campaign.description}
              </p>

              {/* Labels */}
              {campaign.labels && campaign.labels.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {campaign.labels.map((label, index) => {
                    const labelConfig = {
                      reduced_percentage: {
                        color: "bg-red-100 text-red-800",
                        icon: "%",
                      },
                      reduced_amount: {
                        color: "bg-green-100 text-green-800",
                        icon: "kr",
                      },
                      special: {
                        color: "bg-blue-100 text-blue-800",
                        icon: "★",
                      },
                    }[label.labelType];

                    return (
                      <Badge
                        key={index}
                        variant="secondary"
                        className={`text-sm ${labelConfig.color}`}
                      >
                        {labelConfig.icon} {label.text}
                      </Badge>
                    );
                  })}
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{L.start ?? "Start"}:</span>
                  <span>{formatCampaignDateTime(campaign.startDate)}</span>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{L.end ?? "Slut"}:</span>
                  <span>{formatCampaignDateTime(campaign.endDate)}</span>
                </div>
              </div>
            </Card>

            {/* Vehicles Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  {L.vehiclesInCampaign}
                </h3>
                <div className="flex items-center gap-2">
                  {/* Only show "Lägg till annons" button if campaign is not COMPLETED or CANCELLED */}
                  {campaign &&
                    getCampaignStatus(campaign) !== "completed" &&
                    getCampaignStatus(campaign) !== "cancelled" && (
                      <Button
                        onClick={() => setShowCreateListing(true)}
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        {L.addListing}
                      </Button>
                    )}
                  {isLoadingCampaign && (
                    <p className="text-sm text-muted-foreground">Laddar...</p>
                  )}
                </div>
              </div>

              {/* Status filter: Alla default; multi-select for specific statuses */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-xs text-muted-foreground mr-1">
                  {L.show}:
                </span>
                <Button
                  type="button"
                  variant={isAllListingStatuses ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => toggleListingStatus("all")}
                >
                  {L.all ?? "Alla"}
                </Button>
                {LISTING_STATUS_OPTIONS.map(({ key, label }) => (
                  <Button
                    key={key}
                    type="button"
                    variant={
                      !isAllListingStatuses &&
                      selectedListingStatuses.includes(key)
                        ? "default"
                        : "outline"
                    }
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => toggleListingStatus(key)}
                  >
                    {label}
                  </Button>
                ))}
              </div>

              {isLoadingCampaign ? (
                <div className="text-center py-8">
                  <p>Laddar fordon...</p>
                </div>
              ) : totalListings === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">
                    {L.noVehiclesInCampaign ??
                      "Inga fordon tillagda i denna kampanj än."}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <h4 className="font-medium mb-3 text-blue-700">
                    {(L.listingsCount ?? "Annonser ({count})").replace(
                      "{count}",
                      String(totalListings),
                    )}
                  </h4>
                  {displayListings.length === 0 &&
                  totalListings === 0 &&
                  !isLoadingCampaign ? (
                    <p className="text-sm text-muted-foreground py-4">
                      {L.noListingsMatchFilter ??
                        "Inga annonser matchar valda filter. Ändra statusfilter ovan."}
                    </p>
                  ) : (
                    <>
                      <div className="space-y-3">
                        {displayListings.map((listing) => (
                          <Card key={listing.id} className="p-4">
                            <div className="flex items-center gap-4">
                              {/* Thumbnail */}
                              <div className="w-16 h-12 bg-gray-200 rounded-lg flex-shrink-0 overflow-hidden">
                                <img
                                  src={listing.thumbnail}
                                  alt={`${listing.vehicleDetails?.make} ${listing.vehicleDetails?.model}`}
                                  className="w-full h-full object-cover"
                                />
                              </div>

                              {/* Vehicle Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col gap-2 mb-1">
                                  <h5 className="font-medium truncate">
                                    {listing.title}
                                  </h5>
                                  <div className="flex flex-row gap-3">
                                    <p className="text-primary/50">
                                      {listing.vehicleDetails?.make}{" "}
                                      {listing.vehicleDetails?.model}
                                    </p>

                                    <Badge
                                      variant="outline"
                                      className={getVehicleStatusColor(
                                        listing.status,
                                      )}
                                    >
                                      {getVehicleStatusLabel(listing.status)}
                                    </Badge>
                                    <Badge
                                      variant="outline"
                                      className={getTypeColor(
                                        listing.listingType,
                                      )}
                                    >
                                      {getTypeLabel(listing.listingType)}
                                    </Badge>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                  <span>{listing.vehicleDetails?.year}</span>
                                  <span>
                                    {formatMileage(
                                      listing.vehicleDetails?.mileage,
                                    )}{" "}
                                    mil
                                  </span>
                                  <span>
                                    Reg: {listing.vehicleDetails?.regNumber}
                                  </span>
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleGoToListing(listing.id)}
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleShowVehicle(listing.id)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleCancelListingClick(
                                      listing.id,
                                      listing.title,
                                      listing.status,
                                      listing.listingType,
                                    )
                                  }
                                  disabled={
                                    listing.status === "completed" ||
                                    listing.status === "cancelled" ||
                                    isCancellingListing
                                  }
                                  title={
                                    listing.status === "completed"
                                      ? "Kan inte avbryta en såld annons"
                                      : listing.status === "cancelled"
                                        ? "Kan inte avbryta en annons som redan är avbruten"
                                        : (L.cancelListing ?? "Avbryt annons")
                                  }
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                      {totalPages > 1 && (
                        <div className="mt-4">
                          <Pagination
                            currentPage={listingsPage}
                            totalPages={totalPages}
                            totalItems={totalListings}
                            itemsPerPage={listingsLimit}
                            onPageChange={setListingsPage}
                            onItemsPerPageChange={() => {}}
                            itemsPerPageOptions={[10]}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Edit Campaign Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent nested className="max-w-md  max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{L.editCampaign}</DialogTitle>
            <DialogDescription>{L.editCampaignDesc}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{L.campaignName}</Label>
              <Input
                id="name"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{L.description}</Label>
              <Textarea
                id="description"
                value={editForm.description}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">{L.startDate}</Label>
                <DateTimePicker
                  value={editForm.startDate}
                  onChange={(value) =>
                    setEditForm((prev) => ({
                      ...prev,
                      startDate: value ?? "",
                    }))
                  }
                  label=""
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="endDate">{L.endDate}</Label>
                <DateTimePicker
                  value={editForm.endDate}
                  onChange={(value) =>
                    setEditForm((prev) => ({
                      ...prev,
                      endDate: value ?? "",
                    }))
                  }
                  label=""
                  className="w-full"
                />
              </div>
            </div>

            {/* Thumbnail Image */}
            <div className="space-y-2">
              <Label>{L.campaignImageOptional}</Label>
              <div className="flex justify-center">
                {useNativeActionMenu ? (
                  <NativeActionMenu
                    title={L.campaignImageOptional ?? "Kampanjbild"}
                    items={
                      !editForm.shouldRemoveImage &&
                      (editForm.thumbnailPreview ||
                        campaign?.thumbnailImage?.url)
                        ? [
                            {
                              label: L.viewImage,
                              onClick: () =>
                                handleViewImage(
                                  editForm.thumbnailPreview ||
                                    campaign?.thumbnailImage?.url ||
                                    "",
                                ),
                            },
                            {
                              label: L.removeImage,
                              onClick: handleRemoveImage,
                            },
                          ]
                        : [
                            {
                              label: isUpdatingImage ? L.uploading : L.addImage,
                              onClick: handleChangeImage,
                              disabled: isUpdatingImage,
                            },
                          ]
                    }
                    trigger={
                      <Avatar className="h-20 w-20 cursor-pointer relative group">
                        <AvatarImage
                          src={
                            !editForm.shouldRemoveImage &&
                            (editForm.thumbnailPreview ||
                              campaign?.thumbnailImage?.url)
                          }
                        />
                        <AvatarFallback>
                          <Camera className="h-8 w-8" />
                        </AvatarFallback>
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 group-hover:opacity-100 rounded-full cursor-pointer transition-opacity">
                          <MoreVertical className="h-5 w-5" />
                        </div>
                      </Avatar>
                    }
                  />
                ) : (
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Avatar className="h-20 w-20 cursor-pointer relative group">
                        <AvatarImage
                          src={
                            !editForm.shouldRemoveImage &&
                            (editForm.thumbnailPreview ||
                              campaign?.thumbnailImage?.url)
                          }
                        />
                        <AvatarFallback>
                          <Camera className="h-8 w-8" />
                        </AvatarFallback>
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 group-hover:opacity-100 rounded-full cursor-pointer transition-opacity">
                          <MoreVertical className="h-5 w-5" />
                        </div>
                      </Avatar>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="center"
                      className="w-48 z-[230]"
                      sideOffset={8}
                    >
                      {!editForm.shouldRemoveImage &&
                      (editForm.thumbnailPreview ||
                        campaign?.thumbnailImage?.url) ? (
                        <>
                          <DropdownMenuItem
                            onClick={() =>
                              handleViewImage(
                                editForm.thumbnailPreview ||
                                  campaign?.thumbnailImage?.url ||
                                  "",
                              )
                            }
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            {L.viewImage}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleRemoveImage}>
                            <TrashIcon className="mr-2 h-4 w-4" />
                            {L.removeImage}
                          </DropdownMenuItem>
                        </>
                      ) : (
                        <DropdownMenuItem
                          onClick={handleChangeImage}
                          disabled={isUpdatingImage}
                        >
                          <Camera className="mr-2 h-4 w-4" />
                          {isUpdatingImage ? L.uploading : L.addImage}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                <FileUploadTrigger
                  accept="image/*"
                  onFilesSelected={(files) => {
                    if (!files[0]) return;
                    const dt = new DataTransfer();
                    dt.items.add(files[0]);
                    handleFileUpload({
                      target: { files: dt.files },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                >
                  {(open) => {
                    openImagePickerRef.current = open;
                    return null;
                  }}
                </FileUploadTrigger>
              </div>
            </div>

            {/* Labels */}
            <div className="space-y-2">
              <Label>{L.labelsOptionalMax2}</Label>

              {editForm.labels.map((label, index) => (
                <div key={index} className="space-y-2 p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <select
                      value={label.labelType}
                      onChange={(e) =>
                        handleLabelChange(index, "labelType", e.target.value)
                      }
                      className="flex-1 p-2 border rounded-md text-sm bg-card"
                    >
                      {predefinedLabels.map(
                        ({ key, label: labelName, icon: Icon }) => (
                          <option key={key} value={key}>
                            {labelName}
                          </option>
                        ),
                      )}
                    </select>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveLabel(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <Input
                    value={label.text}
                    onChange={(e) =>
                      handleLabelChange(index, "text", e.target.value)
                    }
                    placeholder={L.enterTextMax10}
                    maxLength={10}
                  />
                </div>
              ))}

              {editForm.labels.length < 2 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddLabel}
                >
                  <Tag className="h-4 w-4 mr-1" />
                  {L.addLabel}
                </Button>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              {L.cancel ?? "Avbryt"}
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={isUpdating || isUpdatingImage || isRemovingImage}
            >
              {isUpdating || isUpdatingImage || isRemovingImage
                ? L.saving
                : L.saveChanges}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Open Vehicle Dialog */}
      <ListingDetailsPopup
        isOpen={!!selectedListingId}
        onClose={() => setSelectedListingId(null)}
        listingId={selectedListingId}
      />

      <CreateListingDialog
        open={showCreateListing}
        onOpenChange={setShowCreateListing}
        preSelectedCampaign={campaign}
        preSelectedAdType={campaign?.type}
        onSuccess={() => {
          // Refresh the campaign data after successful creation
          // The getCampaign query will automatically refetch
        }}
      />

      {/* Vehicle Selection Step */}
      <Dialog
        open={showVehicleSelection}
        onOpenChange={setShowVehicleSelection}
      >
        <DialogContent nested className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Välj fordon för kampanj</DialogTitle>
            <DialogDescription>
              Välj ett fordon att lägga till i kampanjen "{campaign?.name}"
            </DialogDescription>
          </DialogHeader>

          <ListingSelectionStep
            onListingSelect={(listing) => {
              // Add listing to campaign directly
              handleAddListingToCampaign(listing.id);
              setShowVehicleSelection(false);
            }}
            onCancel={() => setShowVehicleSelection(false)}
            isStandalone={true}
            campaignType={campaign?.type}
          />
        </DialogContent>
      </Dialog>

      {/* Image Preview Modal */}
      <Dialog open={isImagePreviewOpen} onOpenChange={setIsImagePreviewOpen}>
        <DialogContent nested className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bild</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            <img
              src={previewImageUrl}
              alt="Bild"
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Listing Confirmation Dialog */}
      <AlertDialog
        open={showCancelListingDialog}
        onOpenChange={setShowCancelListingDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {L.cancelListing ?? "Avbryt annons"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {cancelListingInfo && (
                <>
                  Du håller på att avbryta annonsen{" "}
                  <strong>"{cancelListingInfo.listingTitle}"</strong>. Detta går
                  inte att ångra!
                  <br />
                  <br />
                  {cancelListingInfo.hasActiveBids &&
                  cancelListingInfo.bidCount > 0 ? (
                    <>
                      <div className="mt-4 p-3 border border-yellow-400 bg-yellow-50 rounded-md text-yellow-800">
                        <p className="font-semibold">Viktigt:</p>
                        <p className="mt-2">
                          Denna auktion är aktiv och har{" "}
                          <strong>{cancelListingInfo.bidCount}</strong> bud. Om
                          du avbryter annonsen kommer alla som har budat få en
                          notis om att annonsen avbrutits.
                        </p>
                      </div>
                      <br />
                    </>
                  ) : null}
                  Säljaren kommer att få en notis (email + in-app) om att
                  annonsen avbrutits.
                  {cancelListingInfo?.hasActiveBids &&
                    cancelListingInfo.bidCount > 0 && (
                      <> Alla budgivare kommer också att få en notis.</>
                    )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancellingListing}>
              {L.cancel ?? "Avbryt"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancelListing}
              disabled={isCancellingListing}
              className="bg-red-600 hover:bg-red-700"
            >
              {isCancellingListing
                ? (L.cancelling ?? "Avbryter...")
                : (L.yesCancelListing ?? "Ja, avbryt annonsen")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Campaign Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent nested className="max-w-md">
          <DialogHeader>
            <DialogTitle>{L.cancelCampaign ?? "Avbryt kampanj"}</DialogTitle>
            <DialogDescription>
              {campaign?.type === "direct-purchase" ? (
                <>
                  {L.cancelCampaignConfirmDirect
                    ?.replace("{name}", campaign.name)
                    ?.replace("{count}", totalListings.toString()) ??
                    `Du håller på att avbryta ${campaign.name} som har ${totalListings} direktköps annonser. Är du säker? Detta går inte att ångra!`}
                  <br />
                  <br />
                  {L.reservedSoldNotChanged ??
                    "För de annonser som redan är reserverade och sålda i kampanjen kommer inte att ändras. Resterande annonser avbryts!"}
                </>
              ) : (
                <>
                  {L.cancelCampaignConfirmAuction
                    ?.replace("{name}", campaign.name)
                    ?.replace("{count}", totalListings.toString()) ??
                    `Du håller på att avbryta ${campaign.name} som har ${totalListings} auktionsannonser. Är du säker? Detta går inte att ångra!`}
                  <br />
                  <br />
                  {cancelInfo &&
                    cancelInfo.activeAuctionsWithBids.length > 0 && (
                      <>
                        <div className="mt-4 space-y-2">
                          <p className="font-medium">
                            {L.activeAuctionsWithBids ??
                              "Aktiva auktioner med bud:"}
                          </p>
                          {cancelInfo.activeAuctionsWithBids.map(
                            (auction, idx) => (
                              <p key={idx} className="text-sm">
                                •{" "}
                                {L.auctionActiveWithBids
                                  ?.replace("{title}", auction.title)
                                  ?.replace(
                                    "{count}",
                                    auction.bidCount.toString(),
                                  ) ??
                                  `${auction.title} är aktiv och har ${auction.bidCount} bud`}
                              </p>
                            ),
                          )}
                        </div>
                        <br />
                        <p>
                          {L.cancelCampaignBidsNotice ??
                            "Om du avbryter kampanjen, så avbryts även alla inkluderade annonser som ännu inte är reserverade eller sålda. För de aktiva auktioner som har bud kommer alla som budat få en notis om att annonsen avbrutits."}
                        </p>
                      </>
                    )}
                  {(!cancelInfo ||
                    cancelInfo.activeAuctionsWithBids.length === 0) && (
                    <>
                      {L.reservedSoldNotChanged ??
                        "För de annonser som redan är reserverade och sålda i kampanjen kommer inte att ändras. Resterande annonser avbryts!"}
                    </>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCancelDialog(false);
                setCancelInfo(null);
              }}
              disabled={isCancelling}
            >
              {L.cancel ?? "Avbryt"}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmCancel}
              disabled={isCancelling}
            >
              {isCancelling
                ? (L.cancelling ?? "Avbryter...")
                : (L.yesCancelCampaign ?? "Ja, avbryt kampanjen")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start Date Change Confirmation Dialog */}
      <AlertDialog
        open={showStartDateChangeDialog}
        onOpenChange={setShowStartDateChangeDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {L.changeStartDate ?? "Ändra startdatum"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {campaign && (
                <>
                  {L.changeStartDateDesc?.replace("{name}", campaign.name) ??
                    `Du vill ändra startdatumet för ${campaign.name}.`}
                  <br />
                  <br />
                  {L.campaignHasActiveListings ??
                    "Kampanjen har aktiva, reserverade eller utkast-annonser kopplade – dessa kommer avbrytas automatiskt i samband med ändringen."}
                  <br />
                  <br />
                  {L.changeStartDateContinue ?? "Vill du fortsätta?"}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowStartDateChangeDialog(false);
                setPendingUpdateData(null);
              }}
            >
              {L.cancel ?? "Avbryt"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmStartDateChange}
              disabled={isUpdating}
              className="bg-red-600 hover:bg-red-700"
            >
              {isUpdating
                ? (L.updating ?? "Uppdaterar...")
                : (L.changeStartDateConfirm ?? "Ja, ändra startdatum")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
